import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../../components/siteChrome/SiteHeader';
import SiteFooter from '../../components/siteChrome/SiteFooter';
import logo from '../../images/olympiq-logo.svg';
import './compare.css';

type IconKind = 'check' | 'minus' | 'close' | 'soon';

const ICON_CLASS: Record<IconKind, string> = {
  check: 'mdi-check-circle',
  minus: 'mdi-minus-circle-outline',
  close: 'mdi-close-circle-outline',
  soon: 'mdi-progress-clock',
};

const ICON_COLOR_CLASS: Record<IconKind, string> = {
  check: 'oq-compare-icon--check',
  minus: 'oq-compare-icon--minus',
  close: 'oq-compare-icon--close',
  soon: 'oq-compare-icon--soon',
};

/** Accessible names — must match the legend wording exactly (rows ~219–236 below). */
const ICON_LABEL: Record<IconKind, string> = {
  check: 'Fully supported',
  minus: 'Partial / plan-dependent',
  close: 'Not available',
  soon: 'Under development',
};

type FeatureRow = {
  label: string;
  olympiq: IconKind;
  spond: IconKind;
  webCollect: IconKind;
};

/** Comparison table rows — source: mockup L661–677, transcribed row by row. */
const FEATURE_ROWS: FeatureRow[] = [
  {
    label: 'Club website',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'minus',
  },
  {
    label: 'Native iOS & Android app',
    olympiq: 'check',
    spond: 'check',
    webCollect: 'close',
  },
  {
    label: 'One account for website & app',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'close',
  },
  {
    label: 'Session booking & recurring sessions',
    olympiq: 'check',
    spond: 'check',
    webCollect: 'check',
  },
  {
    label: 'Session cancellation refunds',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'check',
  },
  {
    label: 'Automatic membership lifecycle handling',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'check',
  },
  {
    label: 'Trial memberships',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'minus',
  },
  {
    label: 'Club blogs, posts and messaging',
    olympiq: 'check',
    spond: 'minus',
    webCollect: 'close',
  },
  {
    label: 'Club championships',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'close',
  },
  {
    label: 'No fixed monthly or annual fees',
    olympiq: 'check',
    spond: 'check',
    webCollect: 'close',
  },
  {
    label: 'Club shop',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'close',
  },
  {
    label: 'Club kit rental (e.g. bike box hire)',
    olympiq: 'check',
    spond: 'close',
    webCollect: 'close',
  },
];

const FeatureIcon = ({ kind }: { kind: IconKind }): React.JSX.Element => (
  <span role="img" aria-label={ICON_LABEL[kind]}>
    <i className={`mdi ${ICON_CLASS[kind]} ${ICON_COLOR_CLASS[kind]}`} aria-hidden="true" />
  </span>
);

/** Compare page — source: mockup L614–713. */
const Compare = (): React.JSX.Element => (
  <>
    <SiteHeader />
    <main id="main-content" role="main" tabIndex={-1}>
      {/* HERO */}
      <section className="oq-compare-hero">
        <div className="oq-compare-hero__inner">
          <div className="oq-compare-hero__kicker oq-bebas">COMPARE CLUB MANAGEMENT SOFTWARE</div>
          <h1 className="oq-compare-hero__title oq-bebas">
            Olympiq vs Spond &amp; WebCollect
          </h1>
          <p className="oq-compare-hero__subtitle">
            We&apos;re doing things differently — deeply focused on a great experience for your
            members, and on making the admin of running your club our job, not yours.
          </p>
        </div>
      </section>

      {/* WHY SWITCH */}
      <section className="oq-compare-why">
        <div className="oq-container oq-compare-why__inner">
          <div className="oq-compare-why__grid">
            <div className="oq-compare-why__card">
              <div className="oq-compare-why__card-icon">
                <i className="mdi mdi-heart-outline" aria-hidden="true" />
              </div>
              <h3 className="oq-compare-why__card-title oq-bebas">
                An experience members enjoy
              </h3>
              <p className="oq-compare-why__card-body">
                Fast, modern and built for endurance clubs — an app your members actually like
                opening, not a dated portal or a generic team tool.
              </p>
            </div>
            <div className="oq-compare-why__card">
              <div className="oq-compare-why__card-icon">
                <i className="mdi mdi-view-dashboard-outline" aria-hidden="true" />
              </div>
              <h3 className="oq-compare-why__card-title oq-bebas">The admin runs itself</h3>
              <p className="oq-compare-why__card-body">
                Rolling memberships, subs collection, trial periods, refunds and account freezing
                are automated — the admin is on us, not your committee.
              </p>
            </div>
            <div className="oq-compare-why__card">
              <div className="oq-compare-why__card-icon">
                <i className="mdi mdi-wallet-outline" aria-hidden="true" />
              </div>
              <h3 className="oq-compare-why__card-title oq-bebas">£0 fixed fees</h3>
              <p className="oq-compare-why__card-body">
                Pay-as-you-go, with no monthly or annual subscription — you&apos;re never paying
                per member just to exist.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TABLE */}
      <section className="oq-compare-table-section">
        <div className="oq-container oq-compare-table-section__inner">
          <div className="oq-compare-table__card">
            <div className="oq-compare-table__scroll">
              <div className="oq-compare-table__inner">
                <div className="oq-compare-table__head-row">
                  <div className="oq-compare-table__feature-head">
                    <span className="oq-bebas">FEATURE</span>
                  </div>
                  <div className="oq-compare-table__olympiq-head">
                    <img
                      src={logo}
                      alt="Olympiq"
                      width={30}
                      height={30}
                      className="oq-compare-table__olympiq-logo"
                    />
                    <div className="oq-bebas oq-compare-table__olympiq-name">Olympiq</div>
                  </div>
                  <div className="oq-compare-table__competitor-head">
                    <div className="oq-bebas oq-compare-table__competitor-name">Spond</div>
                  </div>
                  <div className="oq-compare-table__competitor-head">
                    <div className="oq-bebas oq-compare-table__competitor-name">WebCollect</div>
                  </div>
                </div>

                {FEATURE_ROWS.map((row, index) => (
                  <div
                    className={`oq-compare-table__row${
                      index % 2 === 0 ? ' oq-compare-table__row--tinted' : ''
                    }`}
                    key={row.label}
                  >
                    <div className="oq-compare-table__feature-label">{row.label}</div>
                    <div className="oq-compare-table__cell oq-compare-table__cell--olympiq">
                      <FeatureIcon kind={row.olympiq} />
                    </div>
                    <div className="oq-compare-table__cell">
                      <FeatureIcon kind={row.spond} />
                    </div>
                    <div className="oq-compare-table__cell">
                      <FeatureIcon kind={row.webCollect} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="oq-compare-table__legend">
              <span className="oq-compare-table__legend-item">
                <i className="mdi mdi-check-circle oq-compare-icon--check" aria-hidden="true" />
                Fully supported
              </span>
              <span className="oq-compare-table__legend-item">
                <i
                  className="mdi mdi-minus-circle-outline oq-compare-icon--minus"
                  aria-hidden="true"
                />
                Partial / plan-dependent
              </span>
              <span className="oq-compare-table__legend-item">
                <i
                  className="mdi mdi-close-circle-outline oq-compare-icon--close"
                  aria-hidden="true"
                />
                Not available
              </span>
            </div>
          </div>

          <p className="oq-compare-table__disclaimer">
            Comparison based on publicly available information as of July 2026. Competitor plans
            and features change — please check each provider&apos;s current offering. Spond and
            WebCollect are trademarks of their respective owners.
          </p>
        </div>
      </section>

      {/* SWITCHING FROM (SEO) */}
      <section className="oq-compare-switch">
        <div className="oq-container oq-compare-switch__inner">
          <h2 className="oq-compare-switch__title oq-bebas">Switching to Olympiq</h2>
          <div className="oq-compare-switch__grid">
            <div className="oq-compare-switch__card">
              <h3 className="oq-compare-switch__card-title oq-bebas">
                A Spond alternative with more under the hood
              </h3>
              <p className="oq-compare-switch__card-body">
                Spond keeps things simple — Olympiq keeps that ease and adds the features clubs
                outgrow it for: session cancellation refunds, trial memberships, and subs that
                collect themselves so no-one is chasing payments.
              </p>
            </div>
            <div className="oq-compare-switch__card">
              <h3 className="oq-compare-switch__card-title oq-bebas">
                A WebCollect alternative, modernised
              </h3>
              <p className="oq-compare-switch__card-body">
                WebCollect is reliable but web-first and dated. Olympiq gives your members an
                easy, modern mobile app — and collects membership renewals automatically, so
                there is far less chasing.
              </p>
            </div>
          </div>
          <div className="oq-compare-switch__cta-row">
            <Link to="/contact-us" className="oq-compare-switch__cta">
              Book a demo &amp; we&apos;ll help you move
            </Link>
          </div>
        </div>
      </section>
    </main>
    <SiteFooter />
  </>
);

export default Compare;
